import { State } from 'app/gamma/src/modules/types';
import {
  AccessLevel,
  MemberModel,
  NotificationEmailFrequency,
  TeamModel,
} from 'app/gamma/src/types/models';
import { memberId } from '@trello/session-cookie';

import { idToDate } from '@trello/dates';
import { getMilliseconds } from '@trello/time';

export const getMemberById = (
  state: State,
  idMember: string,
): MemberModel | undefined => state.models.members[idMember];

export const getMembersByIds = (
  state: State,
  idMembers: string[] = [],
): MemberModel[] =>
  idMembers.reduce((result, idMember) => {
    const member = getMemberById(state, idMember);
    if (member) {
      result.push(member);
    }

    return result;
  }, [] as MemberModel[]);

export const getMemberByFullName = (state: State, fullName: string) =>
  Object.values(state.models.members).find(
    (member: MemberModel) => member.fullName === fullName,
  );

export const getMemberByIdMemberOrUsername = (
  state: State,
  idMemberOrUserName: string,
) =>
  Object.values(state.models.members).find(
    (member: MemberModel) =>
      member.username === idMemberOrUserName ||
      member.id === idMemberOrUserName,
  );

export const getMe = (state: State): MemberModel | undefined =>
  memberId === null ? undefined : state.models.members[memberId];

export const isColorBlind = (state: State): boolean => {
  const user = getMe(state);

  return user && user.prefs ? user.prefs.colorBlind : false;
};

export const isTeamAdmin = (state: State, team: TeamModel | null): boolean => {
  return (
    team !== null &&
    team.memberships !== undefined &&
    team.memberships.some(
      (membership) =>
        membership.memberType === AccessLevel.Admin &&
        membership.idMember === memberId &&
        !membership.deactivated,
    )
  );
};

export const getNotificationEmailFrequency = (state: State) => {
  const me = getMe(state);

  if (!me || !me.prefs) {
    return NotificationEmailFrequency.Instantly;
  }

  return me.prefs.minutesBetweenSummaries;
};

export const isMessageDismissed = (state: State, name: string) => {
  const me = getMe(state);
  if (!me || typeof me.messagesDismissed === 'undefined') {
    return true;
  }

  const messagesDismissed = me.messagesDismissed || [];

  return !!messagesDismissed.find((message) => message.name === name);
};

export const isOneTimeMessageDismissed = (state: State, name: string) => {
  const me = getMe(state);
  if (!me) {
    return false;
  }

  const oneTimeMessagesDismissed = me.oneTimeMessagesDismissed || [];

  return !!oneTimeMessagesDismissed.find((message) => message === name);
};

export const shouldShowMeMarketingOptIn = (state: State) => {
  const me = getMe(state);
  if (!me) {
    return false;
  }

  const isConfirmed = !!me.confirmed;

  const isMoreThanThreeDaysOld =
    new Date().getTime() - idToDate(me.id).getTime() >
    getMilliseconds({ days: 3 });

  const hasSetMarketingOptIn =
    me.marketingOptIn &&
    Object.prototype.hasOwnProperty.call(me.marketingOptIn, 'date');

  return isConfirmed && isMoreThanThreeDaysOld && !hasSetMarketingOptIn;
};

export const shouldShowMeNoticeOfTosChange = (state: State) => {
  const me = getMe(state);
  if (!me) {
    return false;
  }

  const hasDismissedMessage = isMessageDismissed(
    state,
    '1-nov-2018-tos-change-accepted',
  );

  //  Show notice of ToS change if the user's account was created before
  //  November 1, 2018 and the user has not already dismissed the message by
  //  clicking "I Agree"
  return new Date(2018, 10, 1) > idToDate(me.id) && !hasDismissedMessage;
};

export const memberHasOtherNotifications = (state: State) =>
  shouldShowMeMarketingOptIn(state) || shouldShowMeNoticeOfTosChange(state);
